import { type CancelTokenSource } from 'axios'
import { type IReactionDisposer, makeAutoObservable } from 'mobx'
import type { IStatusProps } from 'shared/ui/Status'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { ComplianceApi } from 'entities/Compliance'
import {
  deriveComplianceTiers,
  type IComplianceTab,
  type IComplianceTier,
} from 'entities/Compliance/lib/complianceTiers'
import { type TollFreeProfile } from 'entities/Compliance/model'
import { type IResponseCallingVoiceStatus } from 'entities/Compliance/api/types/voiceServicesTypes'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { PresentationModeStore } from 'widgets/PresentationMode'
import { BusinessProfileModalContent } from 'pages/settings/pages/compliance/ui/BusinessProfileModalContent/BusinessProfileModalContent'
import { EstimatedResponseInfo } from 'pages/settings/pages/compliance/ui/EstimatedResponseInfo/EstimatedResponseInfo'
import { LocalNumbersStepsStore } from 'pages/settings/pages/compliance/pages/localNumbers/store/localNumbersStepsStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { BusinessProfilePageStore } from 'pages/settings/pages/compliance/pages/businessProfile'
import { StatusComplianceIcons } from 'pages/settings/pages/compliance/constants/statusIcon'
import { TollFreePageStore } from 'pages/settings/pages/compliance/pages/tollFree'
import { CallingPageStore } from 'pages/settings/pages/compliance/pages/calling'
import { LocalNumbersPageStore } from 'pages/settings/pages/compliance/pages/localNumbers'
import { InternationalPageStore } from 'pages/settings/pages/compliance/pages/international'
import type { ComplianceLayerStore } from './complianceLayerStore'
import type { IActiveTab } from './types'
import { TollFreeStepsStore } from '../pages/tollFree/store/tollFreeStepsStore'

export class CompliancePageStore {
  private readonly _localNumbersStore: LocalNumbersStepsStore
  private readonly _businessProfilePageStore: BusinessProfilePageStore
  private readonly _tollFreePageStore: TollFreePageStore
  private readonly _tollFreeStepsStore: TollFreeStepsStore
  private readonly _callingPageStore: CallingPageStore
  private readonly _localNumbersPageStore: LocalNumbersPageStore
  private readonly _internationalPageStore: InternationalPageStore
  private _disposePageReaction: IReactionDisposer | null = null
  private _cancelPageSource: CancelTokenSource | null = null

  constructor(
    private _complianceLayerStore: ComplianceLayerStore,
    private _presentationStore: PresentationModeStore,
    private _entities: IEntitiesStore
  ) {
    this._localNumbersStore = new LocalNumbersStepsStore(
      this._complianceLayerStore,
      this._presentationStore,
      this._entities
    )
    this._localNumbersPageStore = new LocalNumbersPageStore(this, this._presentationStore)

    this._businessProfilePageStore = new BusinessProfilePageStore(this)

    this._tollFreePageStore = new TollFreePageStore(this, this._entities)
    this._tollFreeStepsStore = new TollFreeStepsStore(
      this._complianceLayerStore,
      this._presentationStore,
      this._entities
    )

    this._callingPageStore = new CallingPageStore(this, this._entities)

    this._internationalPageStore = new InternationalPageStore(this)

    makeAutoObservable(this)
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()
  }

  get localNumbersStore() {
    return this._localNumbersStore
  }

  get businessProfilePageStore() {
    return this._businessProfilePageStore
  }

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get tollFreePageStore() {
    return this._tollFreePageStore
  }

  get callingPageStore() {
    return this._callingPageStore
  }

  get localNumbersPageStore() {
    return this._localNumbersPageStore
  }

  get complianceLayerStore() {
    return this._complianceLayerStore
  }

  get businessProfile() {
    return this._complianceLayerStore.businessProfile
  }

  get tollFreeProfileItems() {
    return this._complianceLayerStore.tollFreeProfileItems
  }

  get complianceErrors() {
    return this.complianceLayerStore.businessProfile?.twilio_service_errors
  }

  get complianceErrorsMessage() {
    return this.complianceErrors?.map((error) => error.error_message).join('\n')
  }

  get businessInformation() {
    return this.complianceLayerStore.businessInformation
  }

  get internationalPageStore() {
    return this._internationalPageStore
  }

  openSummaryModal = () => {
    const modalId = 'business_profile_summary'
    const iconData = this.getIconConditionByTab('BP')

    modalStore.addModal({
      id: modalId,
      title: 'Business profile',
      width: 540,
      showCloseButton: false,
      hiddenContent: true,
      paddingContent: '0',
      ModalContent: () => {
        return (
          <BusinessProfileModalContent
            statusData={<EstimatedResponseInfo iconData={iconData} />}
            closeModal={() => modalStore.closeModal(modalId)}
            errorMessage={this.complianceErrorsMessage}
            editBusinessProfile={() => {
              uiStore.changeRoute({
                path: `/${SettingsPath.compliance.businessProfile.companyLocation}`,
              })
            }}
            complianceLayerStore={this.complianceLayerStore}
          />
        )
      },
    })
  }

  // Add own methods for calling and toll-free as localNumberCampaignStatus and businessProfileDataStatus
  getIconCondition = ({
    status,
  }: {
    status?: IResponseCallingVoiceStatus | 'alert' | undefined | null
  }) => {
    if (status === 'under_review') {
      return StatusComplianceIcons.carrierReview
    }

    if (status === 'failed') {
      return StatusComplianceIcons.reject
    }

    if (status === 'approved') {
      return StatusComplianceIcons.approved
    }

    if (status === 'alert') {
      return StatusComplianceIcons.alert
    }

    if (status === 'pending') {
      return StatusComplianceIcons.pendingReview
    }

    return null
  }

  get standartVerificationItems(): Record<string, IActiveTab[]> {
    const { summary } = this.complianceLayerStore || {}

    const tiers = deriveComplianceTiers({
      targetCountry: summary?.target_country ?? null,
      hasTaxId: summary?.has_tax_id ?? null,
      numbersType: summary?.numbers_type ?? null,
      isUKCountry: Boolean(this._entities.usersStore.user?.isUKCountry),
      hasUnverifiedTollFree: this.complianceLayerStore.tollFreeUnverifiedNumbers.length > 0,
    })

    const tabsIn = (tier: IComplianceTier): IActiveTab[] =>
      (Object.keys(tiers) as IComplianceTab[]).filter((tab) => tiers[tab] === tier)

    return {
      required: tabsIn('required'),
      recommended: tabsIn('recommended'),
      optional: tabsIn('optional'),
      international: tabsIn('international'),
    }
  }

  get verificationItems(): Record<string, IActiveTab[]> {
    const config = this.standartVerificationItems
    const isSinch = this._entities.organizationStore.isSinchNumberVendor

    return {
      ...config,
      // Sinch organizations do not use the UK bundle flow.
      international: isSinch
        ? config.international.filter((item) => item !== 'UK')
        : config.international,
    }
  }

  get callingStatus() {
    const { cnamServices, statusVoiceIntegrity, statusShakenStir } =
      this._entities.complianceStore.complianceVoiceServicesStore

    const statuses = [
      cnamServices?.cnamServicesStatus,
      statusVoiceIntegrity,
      statusShakenStir,
    ].filter((status) => status !== null && status !== undefined)

    if (statuses.length === 0) return null

    if (statuses.some((status) => status === 'failed')) {
      return StatusComplianceIcons.reject
    }

    if (
      statuses.some((status) =>
        ['under_review', 'in_review', 'pending'].includes(status as IResponseCallingVoiceStatus)
      )
    ) {
      return StatusComplianceIcons.pendingReview
    }

    if (statuses.length === 1) {
      return StatusComplianceIcons.condition(33, 'var(--green-60)')
    }

    if (statuses.length === 2) {
      return StatusComplianceIcons.condition(66, 'var(--green-60)')
    }

    return StatusComplianceIcons.approved
  }

  getIconConditionByTab = (tab: IActiveTab): IStatusProps | null => {
    if (tab === 'BP') return this._businessProfilePageStore.businessProfileDataStatus
    if (tab === 'TF')
      return this.getIconCondition({
        status: this._tollFreePageStore.tollFreeDataStatus,
      })
    if (tab === 'LN') return this._localNumbersPageStore.localNumberDataStatus
    if (tab === 'Calling') return this.callingStatus
    if (tab === 'UK') return this.internationalPageStore.internationalDataStatus

    return null
  }

  getIconInternationalByTab = (tab: IActiveTab) => {
    if (tab === 'UK') return 'flagUK'

    return null
  }

  deleteOrganizationTollFree = async (id: number) => {
    try {
      const response = await ComplianceApi.deleteOrganizationTollFree(id)

      if (response) {
        showToast({
          type: 'success',
          title: 'Toll-free use case deleted',
        })

        await Promise.all([
          this._entities.complianceStore.fetchOrganizationTollFree(),
          this._entities.complianceStore.loadAndUpdateUnverifiedTollFreeNumbers(),
        ])
      }
    } catch (error) {
      logger.error
    }
  }

  openDeleteModal = (item: TollFreeProfile) => {
    const deleteModalId = 'deleteTollFreeNumber'

    if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_toll_free) {
      modalStore.addModal({
        type: ModalTypeList.ALERT,
        id: deleteModalId,
        title: 'Delete toll-free use case progress?',
        desc: 'This action cannot be undone',
        width: 280,
        primaryAction: {
          text: 'Delete',
          onAction: () => {
            this.deleteOrganizationTollFree(item.id)
            modalStore.closeModal(deleteModalId)
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            modalStore.closeModal(deleteModalId)
          },
        },
      })
    } else {
      window.publicApiVue?.onDeleteToolFreeProfileModal(item.origin)
    }
  }
}
